import React from "react";
import { useHistory, useLocation } from "react-router-dom";

type TLocationQueryCount<T extends string> = (options?: { exclude?: T[]; unique?: boolean }) => number;

export interface IUseLocationQuery<T extends string> {
    query: string;
    get: <F extends string>(param: T) => F | null;
    getAll: <F extends string>(param: T) => F[];
    del: <F extends string>(param: T, value?: F) => void;
    set: <F extends string>(param: T, values: F | F[]) => void;
    append: <F extends string>(param: T, values: F[] | F) => void;
    count: TLocationQueryCount<T>;
    clear: (keepKeys?: string[]) => void;
    activeFilters: T[];
}

const useLocationQuery = <T extends string>(): IUseLocationQuery<T> => {
    const history = useHistory();
    const location = useLocation();
    const queryRef = React.useRef<URLSearchParams>(new URLSearchParams(location.search));

    React.useEffect(() => {
        const newParams = new URLSearchParams(location.search);
        if (newParams.toString() !== queryRef.current.toString()) {
            queryRef.current = new URLSearchParams(location.search);
        }
    }, [location.search]);

    const getTemporaryParams = (): URLSearchParams => new URLSearchParams(queryRef.current.toString());

    const updateLocation = (newQuery: URLSearchParams) => {
        const newSearch = newQuery.toString();
        queryRef.current = new URLSearchParams(newSearch);
        history.replace({ search: newSearch });
    };

    const get = <F extends string>(param: T): F | null => queryRef.current.get(param) as F | null;

    const getAll = <F extends string>(param: T): F[] => queryRef.current.getAll(param) as F[];

    const del = <F extends string>(param: T, value?: F): void => {
        const tempQuery = getTemporaryParams();

        if (value === undefined) {
            tempQuery.delete(param);
        } else {
            const filteredParam = tempQuery.getAll(param).filter(f => f !== value);

            tempQuery.delete(param);

            if (filteredParam.length > 0) {
                filteredParam.forEach(filter => tempQuery.append(param, filter));
            }
        }

        updateLocation(tempQuery);
    };

    const set = <F extends string>(param: T, values: F[] | F): void => {
        const tempQuery = getTemporaryParams();

        if (values instanceof Array) {
            if (tempQuery.getAll(param).length > 0) tempQuery.delete(param);
            values.forEach(value => tempQuery.append(param, value));
        } else {
            tempQuery.set(param, values);
        }

        updateLocation(tempQuery);
    };

    const append = <F extends string>(param: T, values: F[] | F): void => {
        const tempQuery = getTemporaryParams();
        const existingParams = tempQuery.getAll(param);

        if (values instanceof Array) {
            const newFilters = values.filter(value => !existingParams.includes(value));
            newFilters.forEach(value => tempQuery.append(param, value));
        } else if (!existingParams.includes(values)) {
            tempQuery.append(param, values);
        }

        updateLocation(tempQuery);
    };

    const count: TLocationQueryCount<T> = (options = {}) => {
        const { exclude = [], unique = false } = options;

        const params = Array.from(new URLSearchParams(queryRef.current));
        const foundParams: string[] = [];

        params.forEach(([param]) => {
            if (exclude.includes(param as T)) return;
            if (unique && foundParams.includes(param)) return;

            foundParams.push(param);
        });

        return foundParams.length;
    };

    const clear = (keepKeys: string[] = []) => {
        const clearedParams = new URLSearchParams();

        keepKeys.forEach(key => {
            queryRef.current.getAll(key).forEach(value => {
                clearedParams.append(key, value);
            });
        });

        updateLocation(clearedParams);
    };

    const activeFilters = [...queryRef.current.keys()] as T[];

    return {
        query: queryRef.current.toString(),
        activeFilters,
        get,
        getAll,
        del,
        set,
        append,
        count,
        clear
    };
};

export default useLocationQuery;
